import React from 'react';
import { Helmet } from 'react-helmet';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import heroImage from '../assets/images/DINK-Social-event.jpg';
import foundersImage from '../assets/images/Charlie-and-Ali.jpg';
import advisorImage from '../assets/images/mitch-160x160.png';
import appStoreBadge from '../assets/images/app-store-badge.svg';
import googlePlayBadge from '../assets/images/google-play-badge.svg';
import { useStoreLinks } from '../lib/useStoreLinks';

const AboutPage: React.FC = () => {
  const { appStoreUrl, playStoreUrl, onAppStoreClick, onPlayStoreClick } = useStoreLinks();
  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>About Us — DINK Social | The Social App for Childfree Couples</title>
        <meta
          name="description"
          content="Meet the founders of DINK Social, the social app built exclusively for childfree couples. Connect with like-minded couples building life on their own terms."
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dinksocial.com/about" />
        <meta property="og:title" content="About Us — DINK Social | The Social App for Childfree Couples" />
        <meta property="og:description" content="Meet the founders of DINK Social, the social app built exclusively for childfree couples. Connect with like-minded couples building life on their own terms." />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://dinksocial.com/about" />
        <meta name="twitter:title" content="About Us — DINK Social | The Social App for Childfree Couples" />
        <meta name="twitter:description" content="Meet the founders of DINK Social, the social app built exclusively for childfree couples. Connect with like-minded couples building life on their own terms." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://dinksocial.com/about" />

        {/* Structured Data - Organization */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://dinksocial.com/#organization",
            "name": "DINK Social",
            "url": "https://dinksocial.com",
            "logo": "https://dinksocial.com/DINKSocial_logo_plum.svg",
            "description": "The first social app built specifically for childfree couples to connect, discover events, and build meaningful friendships.",
            "foundingDate": "2025",
            "sameAs": [
              "https://www.instagram.com/dinksocial",
              "https://www.tiktok.com/@dinksocial"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "hello@dinksocial.com",
              "contactType": "Customer Support"
            }
          }
        `}</script>
      </Helmet>

      <Nav />

      {/* ─── HERO — editorial, white ground ────────────────── */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h1
                className="font-playfair font-medium text-ink text-[36px] md:text-[44px] lg:text-[52px] mb-8"
                style={{ lineHeight: 1.15, letterSpacing: '-0.015em' }}
              >
                The social app built specifically for childfree couples
              </h1>
              <div
                className="space-y-5 text-[17px] md:text-[19px] text-ink/70 mb-10"
                style={{ lineHeight: 1.55 }}
              >
                <p>
                  Some couples choose kids. We chose something different: adventure, connection, and a life built on our own terms.
                </p>
                <p>
                  DINK Social exists for couples like us. The ones craving deeper friendships, spontaneous plans, and spaces that don't revolve around parenting.
                </p>
                <p>
                  Whether you're prioritizing wellness, travel, creativity, or just each other, DINK Social is your place. You're not on the sidelines. You're building something bold, intentional, and entirely your own.
                </p>
              </div>
              <a
                href="#get-the-app"
                className="inline-block text-base font-semibold px-10 py-4 rounded-full bg-primary text-white hover:bg-primary-dark transition-colors"
              >
                Get the app
              </a>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="overflow-hidden rounded-3xl bg-ink/5 w-full max-w-sm">
                <img
                  src={heroImage}
                  alt="DINK Social community event"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHERE WE'RE HEADED — the vision ───────────────── */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2
            className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-8 md:mb-10 text-center"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Where we're headed
          </h2>
          <div
            className="space-y-5 text-[17px] md:text-[19px] text-ink/70"
            style={{ lineHeight: 1.55 }}
          >
            <p>
              We want DINK Social to be the first place couples think of when they choose a childfree life, or end up childfree by circumstance, and start looking for their community. A lot of childfree couples are asking themselves &ldquo;where do we find people like us?&rdquo; We want the answer to be DINK Social.
            </p>
            <p>
              At its heart, this is about connection. We want couples to find real friendship and support here, other couples who actually understand the life they're building together. But we also want to be useful in the practical ways that matter when you're a couple without kids. There are conversations childfree couples need to have and don't have anywhere good: how to care for aging parents, how to retire early, how to handle money as a two-income household, whether you've got a will sorted out. Those things slip down the list when there are no kids forcing the question, and we want DINK Social to be the place couples work through them together and come away feeling ready.
            </p>
            <p>
              The goal is to grow into a household name for this community. We want it to get to the point where a couple mentions they're childfree and the natural next question is &ldquo;oh, are you two on DINK Social?&rdquo; Not big for the sake of being big, but big enough that we can genuinely help dual-income, no-kids couples through every part of their lives together.
            </p>
          </div>
        </div>
      </section>

      {/* ─── WHY WE BUILT IT — editorial story ─────────────── */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-3xl mx-auto mb-16 md:mb-20 text-center">
            <h2
              className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px]"
              style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
            >
              Why we built it
            </h2>
          </div>

          <div className="space-y-16 md:space-y-24">
            {/* Block 1 — The shift */}
            <div className="md:grid md:grid-cols-12 md:gap-10 md:items-start">
              <div className="md:col-span-5">
                <h3
                  className="font-playfair font-medium text-ink text-[34px] md:text-[40px] mb-3"
                  style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
                >
                  The shift
                </h3>
                <p className="text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60">
                  Friends having kids, lives diverging
                </p>
              </div>
              <div className="md:col-span-7 mt-6 md:mt-0">
                <div
                  className="space-y-5 text-[17px] md:text-[19px] text-ink/70"
                  style={{ lineHeight: 1.55 }}
                >
                  <p>
                    As we got older, something started to shift. One by one, our closest friends started having kids, and rightly so, their lives began revolving around their families. Weekends filled up with birthday parties. Spontaneous trips became impossible. Conversations changed.
                  </p>
                  <p>
                    We were happy for them, but we couldn't help feeling like we were drifting to the edge of our own social circles. The things we still wanted, spontaneous travel, late-night hangouts, lazy brunches, real conversations, didn't seem to fit anywhere anymore.
                  </p>
                </div>
              </div>
            </div>

            {/* Block 2 — The disconnect */}
            <div className="md:grid md:grid-cols-12 md:gap-10 md:items-start">
              <div className="md:col-span-5">
                <h3
                  className="font-playfair font-medium text-ink text-[34px] md:text-[40px] mb-3"
                  style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
                >
                  The disconnect
                </h3>
                <p className="text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60">
                  Data said we weren't alone. Reality felt different.
                </p>
              </div>
              <div className="md:col-span-7 mt-6 md:mt-0">
                <div
                  className="space-y-5 text-[17px] md:text-[19px] text-ink/70"
                  style={{ lineHeight: 1.55 }}
                >
                  <p>
                    {'We kept seeing '}
                    <a
                      href="https://www.pewresearch.org/social-trends/2024/07/25/the-experiences-of-u-s-adults-who-dont-have-children/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-dark underline transition-colors"
                    >
                      headlines and stats
                    </a>
                    {" about how more couples were choosing to be childfree. So we knew we weren't alone in how we felt. But in our day-to-day lives? We didn't actually know any other couples like us. And when we looked for a space to meet them, a place truly built for the DINK lifestyle, we couldn't find one."}
                  </p>
                </div>
              </div>
            </div>

            {/* Block 3 — The solution */}
            <div className="md:grid md:grid-cols-12 md:gap-10 md:items-start">
              <div className="md:col-span-5">
                <h3
                  className="font-playfair font-medium text-ink text-[34px] md:text-[40px] mb-3"
                  style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
                >
                  The solution
                </h3>
                <p className="text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60">
                  We built the community we were searching for
                </p>
              </div>
              <div className="md:col-span-7 mt-6 md:mt-0">
                <div
                  className="space-y-5 text-[17px] md:text-[19px] text-ink/70"
                  style={{ lineHeight: 1.55 }}
                >
                  <p>
                    {'That gap between what the data said and what we were actually living is what pushed us to build DINK Social. Our story even caught the attention of '}
                    <a
                      href="https://www.reuters.com/lifestyle/how-one-millennial-couple-turned-no-kids-into-more-freedom-2026-01-21/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-dark underline transition-colors"
                    >
                      Reuters
                    </a>
                    , who featured us in a recent piece on the freedom of the childfree life.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ─── MEET THE FOUNDERS ─────────────────────────────── */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-3xl mx-auto mb-16 md:mb-20 text-center">
            <h2
              className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-5"
              style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
            >
              Meet the founders
            </h2>
            <p
              className="text-[17px] md:text-[19px] text-ink/70"
              style={{ lineHeight: 1.55 }}
            >
              We're a couple who experienced the disconnect firsthand and decided to build the community we were looking for
            </p>
          </div>

          {/* Founders card */}
          <div className="border border-ink/10 rounded-3xl p-8 md:p-12 bg-white mb-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 md:items-start">
              <div className="md:col-span-5">
                <div className="aspect-square w-full rounded-3xl overflow-hidden bg-ink/5">
                  <img
                    src={foundersImage}
                    alt="Charlie and Ali"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="md:col-span-7">
                <h3
                  className="font-display font-medium text-ink text-[28px] md:text-[34px] mb-1"
                  style={{ lineHeight: 1.1, letterSpacing: '-0.015em' }}
                >
                  Charlie & Ali
                </h3>
                <p className="text-[11px] uppercase tracking-[0.22em] font-medium text-primary mb-5">
                  Co-founders
                </p>
                <div
                  className="space-y-4 text-[16px] md:text-[17px] text-ink/70"
                  style={{ lineHeight: 1.55 }}
                >
                  <p>
                    Ali and Charlie met on Hinge in 2018 and were married in 2021. Since then, they've built a life centered around shared values, curiosity, and the freedom to choose their own path. Together, they've traveled to 23 countries, bonded over great food and drinks, and made movie date nights a regular tradition. Along the way, they've called Seattle and Charlotte home and are now based in Amsterdam, never ruling out wherever the next chapter might take them.
                  </p>
                  <p>
                    They're proud DINKWADs to two dogs, Roscoe and Remy, who are truly their entire world. As co-founders of DINK Social, Charlie focuses on business development and building the app itself, while Ali leads community development and social media, growing and connecting the childfree community around the globe.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Advisor card */}
          <div className="border border-ink/10 rounded-3xl p-6 md:p-8 bg-white">
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-ink/5">
                  <img
                    src={advisorImage}
                    alt="Mitch"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3
                  className="font-display font-medium text-ink text-[22px] mb-1"
                  style={{ lineHeight: 1.1 }}
                >
                  Mitch
                </h3>
                <p className="text-[11px] uppercase tracking-[0.22em] font-medium text-primary mb-3">
                  Strategic Advisor
                </p>
                <p
                  className="text-[14px] md:text-[15px] text-ink/70"
                  style={{ lineHeight: 1.55 }}
                >
                  Mitch is a strategic advisor for DINK Social supporting everything from app development to app strategy. Mitch is a 2x startup founder and Forbes 30 Under 30 honoree, he's led both Product and GTM at companies backed by Techstars, 500 Startups, and top VCs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── DOWNLOAD MOMENT ───────────────────────────────── */}
      <section id="get-the-app" className="relative py-10 md:py-14 px-6 md:px-10 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="font-display font-medium text-white text-[38px] md:text-[52px] lg:text-[60px] mb-8 md:mb-10"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            You've found your people
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onAppStoreClick}
              className="hover:opacity-80 transition-opacity"
              aria-label="Download on the App Store"
            >
              <img src={appStoreBadge} alt="Download on the App Store" className="h-12 w-auto" />
            </a>
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onPlayStoreClick}
              className="hover:opacity-80 transition-opacity"
              aria-label="Get it on Google Play"
            >
              <img src={googlePlayBadge} alt="Get it on Google Play" className="h-12 w-auto" />
            </a>
          </div>
        </div>
      </section>

      <Footer bridgeTopSeam />
    </div>
  );
};

export default AboutPage;
